import CryptoJS from 'crypto-js';
import { RuntimeMessages, LinkedinUrl, OfficialWebsite } from "@/config";
import { type MessageType } from "@/libs/chat";
import { getCookie, getCookies } from "./chrome";
import {
  getCookie as getBridgeCookie,
  getCookies as getBridgeCookies,
} from "@/libs/bridge";
import { useI18n } from "@/hooks/i18n";
import { getAppStore } from "./ChromeStore";

// export { translate };

/**
 * 优化后的电子邮件地址标准化函数。
 * 1. 移除开头/结尾的空格 (trim)。
 * 2. 转换为小写 (toLowerCase)。
 * 3. 使用正则表达式，移除 gmail.com 或 googlemail.com 域名前缀中的所有句点 (.)。
 *
 * @param emailAddress 待处理的电子邮件地址。
 * @returns 清理后的电子邮件地址。
 */
export function normalizeEmail(emailAddress: string): string {
  if (!emailAddress || typeof emailAddress !== "string") {
    return "";
  }
  // 步骤 1 & 2: 移除空格并转换为小写
  const lowerTrimmed = emailAddress.trim().toLowerCase();

  // 步骤 3: 使用正则表达式进行标准化
  // 正则表达式解释:
  // (.*) : 捕获组 1，匹配本地部分 (Local Part) 中 @ 符号之前的所有字符
  // @(gmail\.com|googlemail\.com) : 捕获组 2，匹配 @gmail.com 或 @googlemail.com
  const regex = /^(.*)@(gmail\.com|googlemail\.com)$/;

  // 使用 replace() 替换函数来处理匹配项：
  // 如果匹配，则替换函数执行，将本地部分中的句点移除后，再与域名部分重新组合。
  // 如果不匹配（例如是 @outlook.com），则返回原始字符串 lowerTrimmed。
  return lowerTrimmed.replace(regex, (match, localPart, domain) => {
      // 移除本地部分中的所有句点
      const normalizedLocalPart = localPart.replace(/\./g, "");

      // 返回新的标准化邮件地址
      return `${normalizedLocalPart}@${domain}`;
  });
}

export function sha256Sync(message: string) {
  if (!message || typeof message !== "string") {
    return "";
  }
  return CryptoJS.SHA256(message).toString(CryptoJS.enc.Hex);
}

export function useTranslate() {
  let translate: any;
  if (__PLATFORM__ === "extension") {
    // console.log("chrome22222222222222222");
    translate = chrome.i18n?.getMessage;
  } else {
    // console.log("web11111111111111111");
    translate = useI18n().i18n.global.t;
  }
  return translate;
}

type MessageRequestOrigin = "content" | "sidepanel" | undefined;

// import { i18n } from "@/libs/i18n";
// export const translate = chrome.i18n?.getMessage || i18n.global.t;

export function isLinkedInUrl(url: string) {
  const regex = /^https?:\/\/(www|.{2})\.linkedin\.com/;
  return regex.test(url);
}

export type PageType =
  | "profile"
  | "feed"
  | "post"
  | "company"
  | "login"
  | "messages"
  | "search-all"
  | "search-people"
  | "connections"
  | "chrome"
  | "chrome-extension"
  | "view-source"
  | "";
export const pageTypeMap: Record<string, number> = {
  "": 0,
  chrome: 1,
  "chrome-extension": 2,
  "view-source": 3,
  profile: 10,
};
export function getLinkedinPageType(url = "") {
  let type: PageType = "";
  // 先去除 # 及其后面的内容
  const cleanUrl = url.split("#")[0].toLocaleLowerCase();
  return (
    /^https\:\/\/(www|.{2})\.linkedin\.com\/in\/[^/?]+(\/recent-activity\/|\/details\/|\/overlay\/|\/?\?[^/]*|\/?$)/.test(
      cleanUrl
    ) ||
    /^https\:\/\/(www|.{2})\.linkedin\.com\/sales\/people\/(.+?),/.test(
      cleanUrl
    ) ||
    /^https\:\/\/(www|.{2})\.linkedin\.com\/sales\/(profile|lead)\/([^,]{39}),/.test(
      cleanUrl
    )
      ? (type = "profile")
      : cleanUrl.includes("linkedin.com/feed")
      ? (type = "feed")
      : cleanUrl.includes("linkedin.com/posts/")
      ? (type = "post")
      : cleanUrl.includes("linkedin.com/login") ||
        cleanUrl.includes("linkedin.com/checkpoint") ||
        cleanUrl.includes("linkedin.com/authwall")
      ? (type = "login")
      : cleanUrl.includes("linkedin.com/company/")
      ? (type = "company")
      : cleanUrl.includes("linkedin.com/messaging/thread/")
      ? (type = "messages")
      : cleanUrl.includes("linkedin.com/search/results/people/?") ||
        cleanUrl.includes("linkedin.com/sales/search/people")
      ? (type = "search-people")
      : cleanUrl.includes("linkedin.com/search/results/all")
      ? (type = "search-all")
      : cleanUrl.includes("linkedin.com/mynetwork/") && (type = "connections"),
    type
  );
}

export function getProfilePublicId(url: string = "") {
  url = url || window.top?.location.href || document.URL;
  const id =
    (url.match(/linkedin\.com\/in\/([^?/#]+)/) || [])[1] ||
    (url.match(/linkedin\.com\/sales\/people\/(.+?),/) || [])[1] ||
    (url.match(/linkedin\.com\/sales\/(?:profile|lead)\/([^,]{39}),/) ||
      [])[1] ||
    "";
  return decodeURIComponent(id);
}

export function getPostPublicId(url: string = "") {
  url = url || window.top?.location.href || document.URL;
  const id = (url.match(/linkedin\.com\/posts\/([^\/]+)\//) || [])[1] || "";
  return decodeURIComponent(id);
}

export function getCompanyPublicId(url: string = "") {
  url = url || window.top?.location.href || document.URL;
  const id = (url.match(/linkedin\.com\/company\/([^?/]+)/) || [])[1] || "";
  return decodeURIComponent(id);
}

export function getLinkedinPublicId(url: string = "") {
  url = url || window.top?.location.href || document.URL;
  return getProfilePublicId(url) || getCompanyPublicId(url) || "";
}

export function getPageType(url = "") {
  if (!url) return "";
  const protocolMap = Object.keys(pageTypeMap);
  const protocol = new URL(url).protocol.replace(":", "");
  return protocolMap.includes(protocol) ? protocol : getLinkedinPageType(url);
}

export function getCurrentTab() {
  return new Promise<chrome.tabs.Tab>((resolve) => {
    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      resolve(tabs[0]);
    });
  });
}

export function isServiceWorker() {
  return typeof document === "undefined";
}

export function isChromeExtension() {
  return typeof chrome !== "undefined" && typeof chrome.runtime !== "undefined";
}

export function sendMessage(msg: any) {
  return chrome.runtime
    ?.sendMessage(msg)
    .then((res) => {
      return res;
    })
    .catch((e) => {
      console.log("SendMessage Error", msg, e);
    });
}

function sendMessageToTab(tab: chrome.tabs.Tab, msg: any) {
  // console.log("sendMessageToTab", tab, msg);
  return chrome.tabs
    .sendMessage(tab.id!, msg)
    .then(function (response) {
      console.log("Response from content script:", response);
      return response;
    })
    .catch((e) => {
      // console.log(e);
    });
}

export function sendMessageToTabOfContentScript(
  tab: chrome.tabs.Tab,
  msg: any
) {
  // if (!["profile", "feed"].includes(getPageType(tab.url))) {
  //   return;
  // }
  sendMessageToTab(tab, msg);
}

export function sendMessageToActiveTab(msg: any) {
  return chrome.tabs
    .query({ active: true, currentWindow: true })
    .then(function (tabs) {
      if (tabs.length > 0) {
        sendMessageToTabOfContentScript(tabs[0], msg);
      } else {
        console.log("No active tabs found");
      }
    });
}

export function enableSidePanel() {
  return sendMessage({
    action: RuntimeMessages.EnableSidePanel,
  });
}

export function disableSidePanel() {
  return sendMessage({
    action: RuntimeMessages.DisableSidePanel,
  });
}

export function openSidePanel(
  publicId: string,
  msg?: string | MessageType,
  options?: any
) {
  return sendMessage({
    action: RuntimeMessages.OpenSidePanel,
    msg: msg,
    publicId: publicId,
    options,
  });
}

export function closeSidePanel() {
  return sendMessage({
    action: RuntimeMessages.CloseSidePanel,
  });
}

export function openApp(params: any) {
  return sendMessage({
    action: RuntimeMessages.OpenApp,
    ...params,
  });
}

export function openMainWeb({
  pathname,
  query,
  hash,
  newTab = true,
}: {
  pathname?: string;
  query?: Record<string, string> | string;
  hash?: string;
  newTab?: boolean;
} = {}) {
  const url = new URL(import.meta.env.VITE_APP_DOMAIN);
  url.pathname = pathname || "";
  url.search = new URLSearchParams(query).toString();
  url.hash = hash || "";
  if (newTab) {
    window.open(url.toString(), "_blank");
  } else {
    window.location.href = url.toString();
  }
}
export function sendSalesforceSuccess() {
  return sendMessage({
    action: RuntimeMessages.SalesforceSuccess,
  });
}

export function sendGoogleSheetsSuccess() {
  return sendMessage({
    action: RuntimeMessages.GoogleSheetsSuccess,
  });
}

export function sendNotionSuccess() {
  return sendMessage({
    action: RuntimeMessages.NotionSuccess,
  });
}

export function sendHubspotSuccess() {
  return chrome.runtime.sendMessage({
    action: RuntimeMessages.HubspotSuccess,
  });
}

export function sendZohoSuccess() {
  return sendMessage({
    action: RuntimeMessages.ZohoSuccess,
  });
}

export function sendUpgradeSuccess() {
  return sendMessage({
    action: RuntimeMessages.UpgradeSuccess,
  });
}

// export function getUserProfile() {
//   return sendMessage({ action: RuntimeMessages.GetUserProfile });
// }

// export function getSubscriptionInfo() {
//   return sendMessage({
//     action: RuntimeMessages.GetSubscriptionInfo,
//   });
// }

export function updateUserInfo() {
  return sendMessage({
    action: RuntimeMessages.UpdateUserInfo,
  });
}

export function updateUserAuth(
  params: { logged?: boolean; origin?: MessageRequestOrigin } = {}
) {
  return sendMessage({
    action: RuntimeMessages.UpdateUserAuth,
    ...params,
  });
}

// export function getContact(params: { publicId: string }) {
//   return sendMessage({
//     action: RuntimeMessages.GetContact,
//     params: params, // { publicId: publicId },
//   });
// }

// export function getSummary(params: { publicId: string }) {
//   return sendMessage({
//     action: RuntimeMessages.GetSummary,
//     params: params, // { publicId: publicId },
//   });
// }

// export function shortSummarize(
//   params: { publicId: string },
//   init: RequestInit = {}
// ) {
//   return sendMessage({
//     action: RuntimeMessages.ShortSummarize,
//     params: params,
//     init: init,
//   }).then((res) => {
//     return res;
//   });
// }

// export function getContactStatus(params: { publicId: string }) {
//   return sendMessage({
//     action: RuntimeMessages.GetContactStatus,
//     params: params, // { publicId: publicId },
//   });
// }

// export function findEmail(params: any) {
//   return sendMessage({
//     action: RuntimeMessages.FindEmail,
//     params: params,
//   });
// }

// export function oauthSalesforce(params: any = {}) {
//   return sendMessage({
//     action: RuntimeMessages.OauthSalesforce,
//     params: params,
//   });
// }

// export function oauthGoogleSheets(params: any = {}) {
//   return sendMessage({
//     action: RuntimeMessages.OauthGoogleSheets,
//     params: params,
//   });
// }

// export function oauthNotion(params: any = {}) {
//   return sendMessage({
//     action: RuntimeMessages.OauthNotion,
//     params: params,
//   });
// }

// export function oauthHubspot(params: any = {}) {
//   return chrome.runtime.sendMessage({
//     action: RuntimeMessages.OauthHubspot,
//     params: params,
//   });
// }

// export function oauthZoho(params: any = {}) {
//   return sendMessage({
//     action: RuntimeMessages.OauthZoho,
//     params: params,
//   });
// }

// export function contactSync(params: any) {
//   return sendMessage({
//     action: RuntimeMessages.ContactSync,
//     params: params,
//   });
// }

async function getWebCookies(url: string) {
  if (chrome?.cookies) {
    return getCookies(url);
  }
  return getBridgeCookies(url);
}
export async function getWebCookie(url: string, name: string) {
  if (chrome?.cookies) {
    return getCookie(url, name);
  }
  return getBridgeCookie(url, name);
}

// export function openNewWindow(url: string) {
//   return sendMessage({
//     action: RuntimeMessages.OpenNewWindow,
//     params: {
//       url: url,
//       type: "popup",
//       width: 1000,
//       height: 600,
//     },
//   });
// }

export async function getLinkedinCookieParams() {
  const cookies = await getWebCookies(LinkedinUrl);
  const cookie = await getWebCookie(LinkedinUrl, "JSESSIONID");
  return { cookie: cookies, csrfToken: cookie };
}

async function getLandingSource() {
  const lcsource = await getWebCookie(OfficialWebsite, "lcsource");
  return lcsource || "";
}

export async function showTipType(
  pageType: string
): Promise<"Card" | "FloatBtn" | ""> {
  if (pageType !== "profile") {
    return "";
  }
  // const { [StorageKeyMap.HasVisited]: hasVisited } = await storage.get([
  //   StorageKeyMap.HasVisited,
  // ]);
  const AppStore = getAppStore();
  const hasVisited = await AppStore.get("HasVisited");
  if (hasVisited) {
    return "";
  }

  AppStore.set("HasVisited", true);
  // await storage.set({
  //   [StorageKeyMap.HasVisited]: true,
  // });

  const lcsource = await getLandingSource();
  if (
    ["lead-generation", "lead-generation-pt", "generation"].includes(lcsource)
  ) {
    return "FloatBtn";
  } else {
    return "Card";
  }
}

// 第一次访问插件
// export async function firstVisitPlugin(): Promise<boolean> {
//   const { [StorageKeyMap.HasVisited]: hasVisited } = await storage.get([
//     StorageKeyMap.HasVisited,
//   ]);
//   if (hasVisited) return false;
//   await storage.set({
//     [StorageKeyMap.HasVisited]: true,
//   });
//   return true;
// }

// export function getUserSettings(params: any) {
//   return sendMessage({
//     action: RuntimeMessages.GetSettings,
//     params: params,
//   });
// }

// export function updateUserSettings(params: any) {
//   return sendMessage({
//     action: RuntimeMessages.UpdateSettings,
//     params: params,
//   });
// }

// var Utils = (function () {
//   "use strict";
//   return {
//     logDebug: function () {
//       if (window.LinkedhubApi && window.LinkedhubApi.dev)
//         return console.log(
//           "LH[" + new Date().toISOString() + "]: ",
//           ...arguments
//         );
//     },
//     errorDebug: function () {
//       if (window.LinkedhubApi && window.LinkedhubApi.dev)
//         return console.error(
//           "LH[" + new Date().toISOString() + "]: ",
//           ...arguments
//         );
//     },

//     getPageBaseUrl: function (e = "") {
//       const n =
//         /^(https\:\/\/(www|.{2})\.linkedin\.com\/(in|company)\/[^/]+\/)(recent-activity\/|details\/|overlay\/|about\/|posts\/|jobs\/|people\/|videos\/|\?|$)/.exec(
//           e
//         );
//       return n?.[1] || e;
//     },
//     wait: function (e) {
//       return new Promise((n) => setTimeout(n, e));
//     },
//     randomStr: function (e = 10, n = 10) {
//       const t = [
//         ..."ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
//       ];
//       return [...Array(e + Math.floor((n - e) * Math.random()))]
//         .map((e, n) =>
//           0 == n
//             ? t[(Math.random() * (t.length - 10)) | 0]
//             : t[(Math.random() * t.length) | 0]
//         )
//         .join("");
//     },
//     getErrorMessage: function (e, n) {
//       if (e && 0 === e.readyState) return "Network error.";
//       let t =
//         "string" == typeof e
//           ? e
//           : (e &&
//               (e.userMessage ||
//                 e.errorMessage ||
//                 (e.responseJSON && e.responseJSON.message) ||
//                 e.responseText ||
//                 e.message)) ||
//             "";
//       return !t || /(<html>|<title>)/.test(t)
//         ? n || "Unknown Error. Please contact support."
//         : t;
//     },
//   };
// })();

// export function getDateItem(
//   time: number | Date | undefined,
//   type: "year" | "month" | "day" | "hour" | "minute" | "second"
// ) {
//   if (time === undefined) return "";
//   const date = new Date(time);
//   switch (type) {
//     case "year":
//       return date.getFullYear();
//     case "month":
//       return date.getMonth() + 1;
//     case "day":
//       return date.getDate();
//     case "hour":
//       return date.getHours();
//     case "minute":
//       return date.getMinutes();
//     case "second":
//       return date.getSeconds();
//     default:
//       return "";
//   }
// }

export function formatDate(
  time: number | Date | undefined,
  containTime: boolean = false
) {
  if (time === undefined) return "";
  const date = new Date(time);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  // 将年、月、日、小时、分钟、秒数转换为两位数的字符串
  const formattedYear = String(year);
  const formattedMonth = String(month).padStart(2, "0");
  const formattedDay = String(day).padStart(2, "0");

  // 拼接格式化后的日期时间字符串
  let formattedTimestamp = `${formattedYear}-${formattedMonth}-${formattedDay}`;
  if (containTime) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");
    const formattedSeconds = String(seconds).padStart(2, "0");
    formattedTimestamp += ` ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
  }
  return formattedTimestamp;
}

export function formatIntlDate(
  time: number | string | Date | undefined,
  locale: string = "en",
  options: Intl.DateTimeFormatOptions = {},
) {
  if (time === undefined || time === "") return "";
  const timestamp = typeof time === "number" && time < 1e12 ? time * 1000 : time;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  const hasDateOptions = [
    "dateStyle",
    "weekday",
    "era",
    "year",
    "month",
    "day",
  ].some((key) => options[key as keyof Intl.DateTimeFormatOptions] !== undefined);
  const normalizedOptions: Intl.DateTimeFormatOptions = {
    ...(!hasDateOptions ? { dateStyle: "long" as const } : {}),
    ...options,
  };
  return new Intl.DateTimeFormat(locale.replace("_", "-"), normalizedOptions).format(date);
}

// export function downloadFile(url: string, filename: string) {
//   var xhr = new XMLHttpRequest();
//   xhr.open("GET", url, true);
//   xhr.responseType = "blob";
//   xhr.onload = function () {
//     if (xhr.status === 200) {
//       downloadBlob(new Blob([xhr.response]), filename);
//     }
//   };
//   xhr.send();
// }

// export function downloadFile2(url: string, filename?: string) {
//   fetch(url).then((res) =>
//     res.blob().then((blob) => {
//       const file = filename || res.headers.get("Content-Disposition");
//       downloadBlob(blob, file!);
//     })
//   );
// }

export function downloadBlob(blob: Blob | MediaSource, filename: string) {
  const a = document.createElement("a");
  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

// export function createMailtoLink({
//   email,
//   subject,
//   body,
//   cc,
//   bcc,
// }: {
//   email: string;
//   subject: string;
//   body?: string;
//   cc?: string;
//   bcc?: string;
// }) {
//   let link = "mailto:" + encodeURIComponent(email);
//   let params = [];
//   if (subject) {
//     params.push("subject=" + encodeURIComponent(subject));
//   }
//   if (body) {
//     params.push("body=" + encodeURIComponent(body));
//   }
//   if (cc) {
//     params.push("cc=" + encodeURIComponent(cc));
//   }
//   if (bcc) {
//     params.push("bcc=" + encodeURIComponent(bcc));
//   }
//   if (params.length > 0) {
//     link += "?" + params.join("&");
//   }
//   return link;
// }

export function addBreak(dom: HTMLTextAreaElement | HTMLInputElement) {
  var start = dom.selectionStart || dom.value.length;
  var end = dom.selectionEnd || dom.value.length;
  dom.value = dom.value.substring(0, start) + "\n" + dom.value.substring(end);
  dom.selectionStart = dom.selectionEnd = start + 1; // 将光标移动到新插入的换行符后
}

export class CustomError extends Error {
  public code: number;
  constructor(code: number, message: string = "") {
    super(message); // 调用父类的constructor(message)
    this.code = code;
    this.name = "CustomError"; // 可以设置一个更具体的错误名

    // 在TypeScript中，需要修复继承自内置对象的类的原型链
    Object.setPrototypeOf(this, CustomError.prototype);
  }
  // 覆盖toString方法
  public toString(): string {
    return `${this.name}: ${this.message} (code: ${this.code})`;
  }
}

export function generateSafeFilename(username: string): string {
  // 定义一个替换规则的映射对象
  const replaceMap: Record<string, string> = {
    "/": "_",
    "\\": "_",
    ":": "_",
    "*": "_",
    "?": "_",
    '"': "_",
    "<": "_",
    ">": "_",
    "|": "_",
  };

  // 使用正则表达式替换所有特殊字符
  return username.replace(/[\/\\:*?"<>|]/g, (match) => replaceMap[match]);
}

// export function formatCountTime(value: number) {
//   const seconds = Math.floor((value % (1000 * 60)) / 1000);
//   const minutes = Math.floor((value % (1000 * 60 * 60)) / (1000 * 60));
//   const hours = Math.floor((value % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
//   return useTranslate()("discount_countdown", [
//     `${hours}:${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`,
//   ]);
// }

function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export function splitCondition(condition: string) {
  const obj: Record<string, string> = {};
  const pairs = condition.split(" && ");

  pairs.forEach((pair) => {
    const [key, value] = pair.split("=");
    if (key && value) {
      const camelKey = toCamelCase(key.trim());
      obj[camelKey] = value.trim();
    }
  });

  return obj;
}

export function splitName(name: string) {
  // 去掉首尾空格，并用正则分割，避免连续多个空格导致空元素
  const parts = name.trim().split(/\s+/);

  // 如果只有一个部分，返回该部分作为 firstname，lastname 为 null
  if (parts.length === 1) {
    return {
      firstname: parts[0],
    };
  }

  // 如果有多个部分，假设最后一个是 lastname，其余部分是 firstname
  const lastname = parts.pop(); // 移除并返回最后一个元素
  const firstname = parts.join(" "); // 剩余部分作为 firstname

  return {
    firstname: firstname,
    lastname: lastname,
  };
}

// 通用 异步 window.open 解决方案
export function handleAsyncWindowOpen(callBack: Function) {
  if (!callBack || typeof callBack !== "function") return;
  let urlInit: any;
  // 兼容性处理：如果浏览器不支持 requestAnimationFrame，则使用 setTimeout
  const requestAnimFrame =
    window.requestAnimationFrame ||
    function (callback) {
      return setTimeout(callback, 16); // 16ms 大约是 60fps
    };
  const cancelAnimFrame = window.cancelAnimationFrame || clearTimeout;

  const step = () => {
    if (urlInit) {
      window.open(urlInit);
      cancelAnimFrame(frame);
      return;
    }
    requestAnimFrame(step);
  };

  const frame = requestAnimFrame(step);
  callBack((url: string) => {
    urlInit = url;
  });
}

export function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
}

// 检查当前系统语言
export function checkSystemLanguage(language = 'en') {
  const systemLanguage = getSystemLanguage();
  return systemLanguage?.toLowerCase().includes(language);
};

export function getSystemLanguage() {
  // 优先使用系统区域设置
  // 和后端保持一致，使用浏览器语言作为首选
  // const systemLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  // 次选浏览器语言
  const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
  return browserLanguage;
};

// 是否是移动端
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|SymbianOS|IEMobile|Windows Phone|Opera Mini/i.test(
    navigator.userAgent,
  );
}

// 判断当前环境是否为能安装 Chrome 插件的浏览器
export function isChrome(): boolean {
  // 只在浏览器环境下判断
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent;
  // 检查是否为 Chromium 内核浏览器
  const isChromium = /Chrome/.test(ua);
  const isExcluded = /OPR|Brave|Vivaldi/.test(ua);
  return isChromium && !isExcluded;
}
